/**
 * Protocol v2 sync engine (server-authoritative).
 *
 * Replaces the v1 manifest engine wholesale:
 * - **Push before pull**: local dirty records are published first with their
 *   base `version`; the server accepts or reports a per-object conflict.
 * - **Pull is per-object and dirty-aware**: an incoming change for an id with
 *   unpushed local edits is skipped — the eventual push conflict is the
 *   explicit resolution point. Collections are never replaced wholesale
 *   (v1's "stale array overwrote the user's edits" bug).
 * - **Cursor, not manifest diff**: `GET /sync/changes?since=cursor` returns
 *   only what changed; nothing walks the whole library.
 */
import {
  syncApi,
  type ChangesResponse,
  type LocalChange,
  type PushResponse,
  type WireFolder,
  type WireNote,
} from '../api/sync.js';
import type { FolderRecord, NoteRecord } from './dto.js';

export interface PendingDelete {
  kind: 'note' | 'folder';
  id: string;
  deletedAt: number;
  /** Version of the record at delete time — the server conflict-checks it. */
  baseVersion?: number;
}

/** An attachment uploaded on this client, awaiting manifest publication. */
export interface PendingAttachment {
  id: string;
  filename: string;
  hash: string;
  size: number;
  uploadedAt: Date;
}

export interface SyncConflict {
  kind: 'note' | 'folder';
  id: string;
  /** The server's current record (client-shaped) when one exists. */
  theirs: NoteRecord | FolderRecord | null;
}

export interface LibraryStateV2 {
  folders: FolderRecord[];
  notes: NoteRecord[];
  /** Server cursor this library is synced through; 0 = never synced. */
  cursor: number;
  /** `note:<id>` / `folder:<id>` keys with unpushed local edits. */
  dirty: Set<string>;
  /** Hard deletes awaiting server acknowledgement. */
  pendingDeletes: PendingDelete[];
  /** Attachment records to publish with the next push. */
  pendingAttachments: PendingAttachment[];
  /** Conflicts reported by the last push, keyed like `dirty`. */
  conflicts: Map<string, SyncConflict>;
}

export interface SyncOutcomeV2 {
  pushed: number;
  pulled: number;
  /** Conflicts still unresolved after this run. */
  conflicts: SyncConflict[];
  cursor: number;
}

// MARK: wire conversion

function noteToWire(note: NoteRecord): WireNote {
  return {
    id: note.id,
    folderId: note.folderId ?? null,
    title: note.title,
    content: note.content,
    createdAt: note.createdAt.getTime(),
    updatedAt: note.updatedAt.getTime(),
    deletedAt: note.deletedAt ? note.deletedAt.getTime() : null,
    trashedFrom: note.trashedFrom ?? null,
    isVaulted: note.isVaulted,
    vaultCiphertextBase64: note.vaultCiphertextBase64 ?? null,
    vaultedFrom: note.vaultedFrom ?? null,
    // Unknown (v1-migrated) records start their server life at version 1.
    version: Math.max(note.version ?? 1, 1),
    revision: 0,
  };
}

function noteFromWire(wire: WireNote): NoteRecord {
  return {
    id: wire.id,
    folderId: wire.folderId,
    title: wire.title,
    content: wire.content,
    createdAt: new Date(wire.createdAt),
    updatedAt: new Date(wire.updatedAt),
    deletedAt: wire.deletedAt !== null ? new Date(wire.deletedAt) : null,
    trashedFrom: wire.trashedFrom,
    isVaulted: wire.isVaulted,
    vaultCiphertextBase64: wire.vaultCiphertextBase64,
    vaultedFrom: wire.vaultedFrom,
    version: wire.version,
  };
}

function folderToWire(folder: FolderRecord): WireFolder {
  return {
    id: folder.id,
    name: folder.name,
    parentId: folder.parentId ?? null,
    sortOrder: folder.sortOrder ?? null,
    createdAt: folder.createdAt.getTime(),
    updatedAt: folder.updatedAt.getTime(),
    version: Math.max(folder.version ?? 1, 1),
    revision: 0,
  };
}

function folderFromWire(wire: WireFolder): FolderRecord {
  return {
    id: wire.id,
    name: wire.name,
    parentId: wire.parentId,
    sortOrder: wire.sortOrder,
    createdAt: new Date(wire.createdAt),
    updatedAt: new Date(wire.updatedAt),
    version: wire.version,
  };
}

// MARK: engine

function dirtyKey(kind: 'note' | 'folder', id: string): string {
  return `${kind}:${id}`;
}

/** Collects dirty records + pending deletes/attachments into a push batch. */
export function buildPushBatch(state: LibraryStateV2): LocalChange[] {
  const changes: LocalChange[] = [];
  for (const note of state.notes) {
    if (state.dirty.has(dirtyKey('note', note.id))) changes.push({ kind: 'note', op: 'upsert', record: noteToWire(note) });
  }
  for (const folder of state.folders) {
    if (state.dirty.has(dirtyKey('folder', folder.id))) changes.push({ kind: 'folder', op: 'upsert', record: folderToWire(folder) });
  }
  for (const del of state.pendingDeletes) {
    changes.push({
      kind: del.kind,
      op: 'delete',
      id: del.id,
      deletedAt: del.deletedAt,
      ...(del.kind === 'note'
        ? { baseVersion: Math.max(del.baseVersion ?? findVersion(state, 'note', del.id) ?? 1, 1) }
        : {}),
    });
  }
  for (const attachment of state.pendingAttachments) {
    changes.push({
      kind: 'attachment',
      op: 'upsert',
      record: {
        id: attachment.id,
        filename: attachment.filename,
        hash: attachment.hash,
        size: attachment.size,
        updatedAt: attachment.uploadedAt.getTime(),
      },
    });
  }
  return changes;
}

function findVersion(state: LibraryStateV2, kind: 'note' | 'folder', id: string): number | undefined {
  if (kind === 'note') return state.notes.find((note) => note.id === id)?.version;
  return state.folders.find((folder) => folder.id === id)?.version;
}

function applyPushResult(state: LibraryStateV2, result: PushResponse): number {
  let pushed = 0;
  for (const entry of result.accepted) {
    pushed += 1;
    if (entry.kind === 'note' || entry.kind === 'folder') {
      const key = dirtyKey(entry.kind, entry.id);
      state.dirty.delete(key);
      state.conflicts.delete(key);
      if (entry.version > 0) {
        const record =
          entry.kind === 'note'
            ? state.notes.find((note) => note.id === entry.id)
            : state.folders.find((folder) => folder.id === entry.id);
        if (record) record.version = entry.version;
      }
    }
  }
  state.pendingDeletes = state.pendingDeletes.filter(
    (del) => !result.accepted.some((entry) => entry.kind === del.kind && entry.id === del.id),
  );
  if (state.pendingAttachments.length > 0) {
    state.pendingAttachments = state.pendingAttachments.filter((attachment) =>
      !result.accepted.some(
        (entry) => entry.kind === 'attachment' && entry.id === `${attachment.id}/${attachment.filename}`,
      ),
    );
  }
  for (const conflict of result.conflicts) {
    if (conflict.kind !== 'note' && conflict.kind !== 'folder') continue;
    const theirs =
      conflict.kind === 'note'
        ? conflict.theirs && 'content' in conflict.theirs
          ? noteFromWire(conflict.theirs as WireNote)
          : null
        : conflict.theirs && 'name' in conflict.theirs
          ? folderFromWire(conflict.theirs as WireFolder)
          : null;
    state.conflicts.set(dirtyKey(conflict.kind, conflict.id), {
      kind: conflict.kind,
      id: conflict.id,
      theirs,
    });
  }
  return pushed;
}

/** Applies one feed page; returns how many records were applied. */
function applyFeed(state: LibraryStateV2, feed: ChangesResponse): number {
  let applied = 0;
  for (const change of feed.changes) {
    if (change.kind === 'note' || change.kind === 'folder') {
      const key = dirtyKey(change.kind, change.objectId);
      if (state.dirty.has(key)) continue; // local edit pending — conflict flow owns it
      if (change.kind === 'note') {
        if (change.op === 'delete') {
          const before = state.notes.length;
          state.notes = state.notes.filter((note) => note.id !== change.objectId);
          if (state.notes.length !== before) applied += 1;
        } else if (change.record && typeof change.record === 'object') {
          const record = noteFromWire(change.record as WireNote);
          const at = state.notes.findIndex((note) => note.id === record.id);
          if (at >= 0) state.notes[at] = record;
          else state.notes.push(record);
          applied += 1;
        }
      } else {
        if (change.op === 'delete') {
          const before = state.folders.length;
          state.folders = state.folders.filter((folder) => folder.id !== change.objectId);
          if (state.folders.length !== before) applied += 1;
        } else if (change.record && typeof change.record === 'object') {
          const record = folderFromWire(change.record as WireFolder);
          const at = state.folders.findIndex((folder) => folder.id === record.id);
          if (at >= 0) state.folders[at] = record;
          else state.folders.push(record);
          applied += 1;
        }
      }
    }
    // attachment / vault_meta / vault_dek entries need no client-side state:
    // attachment bytes live on the bucket and vault unlocking is server-side.
  }
  state.cursor = feed.nextCursor;
  return applied;
}

/** The API surface the engine needs (satisfied by `syncApi`, faked in tests). */
export interface SyncApiLike {
  push(changes: LocalChange[]): Promise<PushResponse>;
  changes(since: number, limit?: number): Promise<ChangesResponse>;
  resolve(
    kind: 'note' | 'folder',
    id: string,
    strategy: 'keep_mine' | 'take_theirs' | 'fork_copy',
    record?: unknown,
  ): Promise<{ note?: WireNote; folder?: WireFolder; copyId?: string }>;
}

export interface SyncOptionsV2 {
  /** Skip the pull phase (e.g. right after a resolve that re-pulls anyway). */
  pushOnly?: boolean;
  /** Injectable for tests. */
  api?: SyncApiLike;
  onProgress?: (phase: 'pushing' | 'pulling', done: number, total: number) => void;
}

/**
 * Runs one full sync: push dirty → pull incremental → update cursor.
 * Mutates `state` in place (the caller owns persistence and rendering).
 */
export async function runSyncV2(
  state: LibraryStateV2,
  options: SyncOptionsV2 = {},
): Promise<SyncOutcomeV2> {
  const api = options.api ?? syncApi;
  let pushed = 0;
  let pulled = 0;

  const batch = buildPushBatch(state);
  if (batch.length > 0) {
    options.onProgress?.('pushing', 0, batch.length);
    const result = await api.push(batch);
    pushed = applyPushResult(state, result);
    options.onProgress?.('pushing', batch.length, batch.length);
  }

  if (!options.pushOnly) {
    for (let guard = 0; ; guard += 1) {
      const feed = await api.changes(state.cursor);
      options.onProgress?.('pulling', pulled, pulled + feed.changes.length);
      pulled += applyFeed(state, feed);
      if (!feed.hasMore) break;
      if (guard > 10_000) throw new Error('sync feed did not converge');
    }
  }

  return { pushed, pulled, conflicts: [...state.conflicts.values()], cursor: state.cursor };
}

/**
 * Resolves a conflict and updates local state to match the outcome.
 * `mine` is the local (dirty) record for keep_mine / fork_copy.
 */
export async function resolveConflictV2(
  state: LibraryStateV2,
  conflict: SyncConflict,
  strategy: 'keep_mine' | 'take_theirs' | 'fork_copy',
  options: { api?: SyncApiLike } = {},
): Promise<void> {
  const api = options.api ?? syncApi;
  const key = dirtyKey(conflict.kind, conflict.id);
  const mine =
    conflict.kind === 'note'
      ? state.notes.find((note) => note.id === conflict.id)
      : state.folders.find((folder) => folder.id === conflict.id);

  if (strategy === 'take_theirs') {
    await api.resolve(conflict.kind, conflict.id, 'take_theirs');
    // Drop the local edit and adopt the server copy (already in `theirs`,
    // or arriving with the next pull).
    if (conflict.kind === 'note') {
      if (conflict.theirs && 'content' in conflict.theirs) {
        const record = conflict.theirs as NoteRecord;
        const at = state.notes.findIndex((note) => note.id === conflict.id);
        if (at >= 0) state.notes[at] = { ...record, version: record.version };
      }
    } else if (conflict.theirs && 'name' in conflict.theirs) {
      const record = conflict.theirs as FolderRecord;
      const at = state.folders.findIndex((folder) => folder.id === conflict.id);
      if (at >= 0) state.folders[at] = { ...record, version: record.version };
    }
    state.dirty.delete(key);
    state.conflicts.delete(key);
    return;
  }

  if (!mine) throw new Error('conflict record missing locally');
  const record =
    conflict.kind === 'note'
      ? noteToWire(mine as NoteRecord)
      : folderToWire(mine as FolderRecord);
  const result = await api.resolve(conflict.kind, conflict.id, strategy, record);

  if (conflict.kind === 'note' && result.note) {
    const updated = noteFromWire(result.note);
    const at = state.notes.findIndex((note) => note.id === conflict.id);
    if (at >= 0) state.notes[at] = updated;
    mine.version = updated.version;
  } else if (conflict.kind === 'folder' && result.folder) {
    const updated = folderFromWire(result.folder);
    const at = state.folders.findIndex((folder) => folder.id === conflict.id);
    if (at >= 0) state.folders[at] = updated;
    (mine as FolderRecord).version = updated.version;
  }
  state.dirty.delete(key);
  state.conflicts.delete(key);
  // fork_copy created a sibling copy server-side; the next pull picks it up.
}

/** Marks a record as locally edited ( joins the next push batch). */
export function markDirty(state: LibraryStateV2, kind: 'note' | 'folder', id: string): void {
  state.dirty.add(dirtyKey(kind, id));
  state.conflicts.delete(dirtyKey(kind, id));
}

/** Queues a hard delete (removes locally now, publishes with the next push). */
export function queueDelete(
  state: LibraryStateV2,
  kind: 'note' | 'folder',
  id: string,
  now = Date.now(),
): void {
  // Capture the version before the record leaves the arrays — the server
  // conflict-checks deletes against it.
  const baseVersion = findVersion(state, kind, id);
  state.pendingDeletes.push({ kind, id, deletedAt: now, ...(baseVersion !== undefined ? { baseVersion } : {}) });
  if (kind === 'note') state.notes = state.notes.filter((note) => note.id !== id);
  else state.folders = state.folders.filter((folder) => folder.id !== id);
}
